/**
 * Consultations Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive consultation management interface with advanced filtering, search, and display capabilities.
 * Provides both card and table views of consultation data with smart filtering, pagination, and real-time statistics.
 * 
 * Features:
 * - Smart filtering with dynamic filter options (ETF-style)
 * - Advanced search with highlighting
 * - Dual view modes (Cards/Table)
 * - Pagination with configurable page sizes
 * - Real-time statistics and applied filters display
 * - Daire AI integration
 * - Chart visualization
 * - Comprehensive filter management
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of consultation objects to display
 * @param {boolean} [props.showChart=true] - Whether to show the chart at the top
 * @param {string} [props.initialSearchQuery=''] - Initial search query
 * @param {boolean} [props.showSearch=true] - Whether to show search functionality
 * @param {boolean} [props.showCount=false] - Whether to show count information
 * @param {boolean} [props.showReset=true] - Whether to show reset button
 * @returns {JSX.Element} The rendered consultations component
 * 
 * @example
 * ```jsx
 * <Consultations 
 *   data={consultationData} 
 *   showChart={true}
 *   initialSearchQuery=""
 *   showSearch={true}
 *   showReset={true}
 * />
 * ```
 */

import React, { useEffect, useState, useMemo } from 'react';
import ConsultationsTable from './ConsultationsTable';
import ConsultationsCards from './ConsultationCards';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import Parser from 'html-react-parser';
import useFetchPageContent from '../../hooks/useFetchPageContent';
import ConsultationsChart from './ConsultationsChart';
import './Consultations.css';
import DaireSidebar from '../Daire/DaireSidebar';


function Consultations({ data, showChart = true, initialSearchQuery ='', showSearch = true, showCount = false, showReset = true }) {
    const [years, setYears] = useState([]);
    const [issuingBodies, setIssuingBodies] = useState([]);
    const [statuses] = useState(['Completed', 'Live', 'Pending']);
    const [categories, setCategories] = useState([]);

    const [selectedYear, setSelectedYear] = useState('Year (ALL)');
    const [selectedStatus, setSelectedStatus] = useState('Status (ALL)');
    const [selectedCategory, setSelectedCategory] = useState('Category (ALL)');
    const [selectedIssuingBody, setSelectedIssuingBody] = useState('Issuing Body (ALL)');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const cspBlobContent = useFetchPageContent(1);

    const [filteredYears, setFilteredYears] = useState([]);
    const [filteredIssuingBodies, setFilteredIssuingBodies] = useState([]);
    const [filteredStatuses, setFilteredStatuses] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);

    const [isDaireOpen, setIsDaireOpen] = useState(false);
    const [isDaireMinimized, setIsDaireMinimized] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(6); // Items per page
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [loadingPageNumber, setLoadingPageNumber] = useState(1);

    const parserOptions = {
        replace: (domNode) => {
            if (domNode.type === 'text' && domNode.data.includes('Ask Daire!')) {
                return (
                    <span 
                        className="ask-daire-link" 
                        onClick={() => setIsDaireOpen(true)}
                    >
                        Ask Daire!
                    </span>
                );
            }
        }
    };

    useEffect(() => {
        if (initialSearchQuery) {
            handleSearchChange(initialSearchQuery);
        }
    }, [initialSearchQuery]);

    // Initialize unique options based on data
    useEffect(() => {
        if (data) {
            const uniqueYears = [...new Set(data.map(item => item.year))].sort((a, b) => a - b);
            const uniqueIssuingBodies = [...new Set(data.map(item => item.ib))].sort();
            const uniqueCategories = [...new Set(data.map(item => item.category))].sort();

            setYears(['Year (ALL)', ...uniqueYears]);
            setIssuingBodies(['Issuing Body (ALL)', ...uniqueIssuingBodies]);
            setCategories(['Category (ALL)', ...uniqueCategories]);
            setFilteredYears(['Year (ALL)', ...uniqueYears]);
            setFilteredIssuingBodies(['Issuing Body (ALL)', ...uniqueIssuingBodies]);
            setFilteredStatuses(['Status (ALL)', ...statuses]);
            setFilteredCategories(['Category (ALL)', ...uniqueCategories]);
        }
    }, [data]);

    // Dynamic filter options are now handled by getDynamicFilterOptions() function below

    /**
     * Generate dynamic filter options based on current selections
     * 
     * Implements ETF-style smart filtering where each filter dropdown only shows
     * options that would return results given the current state of other filters.
     * This prevents users from selecting combinations that would yield no results.
     * 
     * @returns {Object} Object containing arrays of available options for each filter
     * @returns {Array} returns.years - Available years based on current filters
     * @returns {Array} returns.issuingBodies - Available issuing bodies based on current filters
     * @returns {Array} returns.statuses - Available statuses based on current filters
     * @returns {Array} returns.categories - Available categories based on current filters
     * 
     * @example
     * ```javascript
     * const options = getDynamicFilterOptions();
     * // Returns: { years: ['Year (ALL)', 2023, 2024], issuingBodies: [...], ... }
     * ```
     */
    const getDynamicFilterOptions = () => {
        if (!data) return {
            years: ['Year (ALL)'],
            issuingBodies: ['Issuing Body (ALL)'],
            statuses: ['Status (ALL)', ...statuses],
            categories: ['Category (ALL)']
        };
        
        // For Years: filter by everything EXCEPT year selection
        let yearData = [...data];
        if (selectedIssuingBody !== 'Issuing Body (ALL)') {
            yearData = yearData.filter(item => item.ib === selectedIssuingBody);
        }
        if (selectedStatus !== 'Status (ALL)') {
            yearData = yearData.filter(item => item.status === selectedStatus);
        }
        if (selectedCategory !== 'Category (ALL)') {
            yearData = yearData.filter(item => item.category === selectedCategory);
        }
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            yearData = yearData.filter(item =>
                item.name.toLowerCase().includes(lowerCaseQuery) ||
                item.topic.toLowerCase().includes(lowerCaseQuery) ||
                item.category.toLowerCase().includes(lowerCaseQuery) ||
                item.ib?.toLowerCase().includes(lowerCaseQuery) ||
                item.wg?.toLowerCase().includes(lowerCaseQuery) ||
                item.status?.toLowerCase().includes(lowerCaseQuery)
            );
        }
        
        // For Issuing Bodies: filter by everything EXCEPT issuing body selection
        let issuingBodyData = [...data];
        if (selectedYear !== 'Year (ALL)') {
            issuingBodyData = issuingBodyData.filter(item => item.year === parseInt(selectedYear, 10));
        }
        if (selectedStatus !== 'Status (ALL)') {
            issuingBodyData = issuingBodyData.filter(item => item.status === selectedStatus);
        }
        if (selectedCategory !== 'Category (ALL)') {
            issuingBodyData = issuingBodyData.filter(item => item.category === selectedCategory);
        }
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            issuingBodyData = issuingBodyData.filter(item =>
                item.name.toLowerCase().includes(lowerCaseQuery) ||
                item.topic.toLowerCase().includes(lowerCaseQuery) ||
                item.category.toLowerCase().includes(lowerCaseQuery) ||
                item.ib?.toLowerCase().includes(lowerCaseQuery) ||
                item.wg?.toLowerCase().includes(lowerCaseQuery) ||
                item.status?.toLowerCase().includes(lowerCaseQuery)
            );
        }
        
        // For Status: filter by everything EXCEPT status selection
        let statusData = [...data];
        if (selectedYear !== 'Year (ALL)') {
            statusData = statusData.filter(item => item.year === parseInt(selectedYear, 10));
        }
        if (selectedIssuingBody !== 'Issuing Body (ALL)') {
            statusData = statusData.filter(item => item.ib === selectedIssuingBody);
        }
        if (selectedCategory !== 'Category (ALL)') {
            statusData = statusData.filter(item => item.category === selectedCategory);
        }
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            statusData = statusData.filter(item =>
                item.name.toLowerCase().includes(lowerCaseQuery) ||
                item.topic.toLowerCase().includes(lowerCaseQuery) ||
                item.category.toLowerCase().includes(lowerCaseQuery) ||
                item.ib?.toLowerCase().includes(lowerCaseQuery) ||
                item.wg?.toLowerCase().includes(lowerCaseQuery) ||
                item.status?.toLowerCase().includes(lowerCaseQuery)
            );
        }
        
        // For Categories: filter by everything EXCEPT category selection
        let categoryData = [...data];
        if (selectedYear !== 'Year (ALL)') {
            categoryData = categoryData.filter(item => item.year === parseInt(selectedYear, 10));
        }
        if (selectedIssuingBody !== 'Issuing Body (ALL)') {
            categoryData = categoryData.filter(item => item.ib === selectedIssuingBody);
        }
        if (selectedStatus !== 'Status (ALL)') {
            categoryData = categoryData.filter(item => item.status === selectedStatus);
        }
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            categoryData = categoryData.filter(item =>
                item.name.toLowerCase().includes(lowerCaseQuery) ||
                item.topic.toLowerCase().includes(lowerCaseQuery) ||
                item.category.toLowerCase().includes(lowerCaseQuery) ||
                item.ib?.toLowerCase().includes(lowerCaseQuery) ||
                item.wg?.toLowerCase().includes(lowerCaseQuery) ||
                item.status?.toLowerCase().includes(lowerCaseQuery)
            );
        }
        
        // Extract unique values for each filter
        const dynamicOptions = {
            years: [...new Set(yearData.map(item => item.year))].filter(year => year != null).sort((a, b) => a - b),
            issuingBodies: [...new Set(issuingBodyData.map(item => item.ib))].filter(Boolean).sort(),
            statuses: [...new Set(statusData.map(item => item.status))].filter(Boolean).sort(),
            categories: [...new Set(categoryData.map(item => item.category))].filter(Boolean).sort()
        };
        
        // Always include currently selected options (so they don't disappear from dropdowns)
        if (selectedYear !== 'Year (ALL)' && !dynamicOptions.years.includes(parseInt(selectedYear, 10))) {
            dynamicOptions.years.push(parseInt(selectedYear, 10));
            dynamicOptions.years.sort((a, b) => a - b);
        }
        if (selectedIssuingBody !== 'Issuing Body (ALL)' && !dynamicOptions.issuingBodies.includes(selectedIssuingBody)) {
            dynamicOptions.issuingBodies.push(selectedIssuingBody);
            dynamicOptions.issuingBodies.sort();
        }
        if (selectedStatus !== 'Status (ALL)' && !dynamicOptions.statuses.includes(selectedStatus)) {
            dynamicOptions.statuses.push(selectedStatus);
            dynamicOptions.statuses.sort();
        }
        if (selectedCategory !== 'Category (ALL)' && !dynamicOptions.categories.includes(selectedCategory)) {
            dynamicOptions.categories.push(selectedCategory);
            dynamicOptions.categories.sort();
        }
        
        // Add "ALL" options at the beginning
        return {
            years: ['Year (ALL)', ...dynamicOptions.years],
            issuingBodies: ['Issuing Body (ALL)', ...dynamicOptions.issuingBodies],
            statuses: ['Status (ALL)', ...dynamicOptions.statuses],
            categories: ['Category (ALL)', ...dynamicOptions.categories]
        };
    };

    /**
     * Smart filter change handler with automatic dependent filter reset
     * 
     * Handles filter changes using ETF-style smart filtering. When a filter changes,
     * it automatically resets other filters if their current values would no longer
     * return any results. This prevents users from getting stuck in no-results states.
     * 
     * @param {string} filterType - Type of filter being changed ('year', 'issuingBody', 'status', 'category')
     * @param {string} value - New value for the filter
     * 
     * @example
     * ```javascript
     * handleSmartFilterChange('year', '2023');
     * handleSmartFilterChange('category', 'Consultation');
     * ```
     */
    const handleSmartFilterChange = (filterType, value) => {
        if (!data) return;
        
        let filteredData = [...data];
        
        // Apply the new filter value
        if (filterType === 'year' && value !== 'Year (ALL)') {
            filteredData = filteredData.filter(item => item.year === parseInt(value, 10));
        } else if (selectedYear !== 'Year (ALL)' && filterType !== 'year') {
            filteredData = filteredData.filter(item => item.year === parseInt(selectedYear, 10));
        }

        if (filterType === 'issuingBody' && value !== 'Issuing Body (ALL)') {
            filteredData = filteredData.filter(item => item.ib === value);
        } else if (selectedIssuingBody !== 'Issuing Body (ALL)' && filterType !== 'issuingBody') {
            filteredData = filteredData.filter(item => item.ib === selectedIssuingBody);
        }

        if (filterType === 'status' && value !== 'Status (ALL)') {
            filteredData = filteredData.filter(item => item.status === value);
        } else if (selectedStatus !== 'Status (ALL)' && filterType !== 'status') {
            filteredData = filteredData.filter(item => item.status === selectedStatus);
        }

        if (filterType === 'category' && value !== 'Category (ALL)') {
            filteredData = filteredData.filter(item => item.category === value);
        } else if (selectedCategory !== 'Category (ALL)' && filterType !== 'category') {
            filteredData = filteredData.filter(item => item.category === selectedCategory);
        }

        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            filteredData = filteredData.filter(item =>
                item.name.toLowerCase().includes(lowerCaseQuery) ||
                item.topic.toLowerCase().includes(lowerCaseQuery) ||
                item.category.toLowerCase().includes(lowerCaseQuery) ||
                item.ib?.toLowerCase().includes(lowerCaseQuery) ||
                item.wg?.toLowerCase().includes(lowerCaseQuery) ||
                item.status?.toLowerCase().includes(lowerCaseQuery)
            );
        }

        // Get available options after this filter change
        const availableOptions = {
            years: [...new Set(filteredData.map(item => item.year))].filter(year => year != null),
            issuingBodies: [...new Set(filteredData.map(item => item.ib))].filter(Boolean),
            statuses: [...new Set(filteredData.map(item => item.status))].filter(Boolean),
            categories: [...new Set(filteredData.map(item => item.category))].filter(Boolean)
        };
        
        // Apply the main filter change
        if (filterType === 'year') {
            setSelectedYear(value);
        } else if (filterType === 'issuingBody') {
            setSelectedIssuingBody(value);
        } else if (filterType === 'status') {
            setSelectedStatus(value);
        } else if (filterType === 'category') {
            setSelectedCategory(value);
        }
        
        // Reset dependent filters if their current value is no longer available
        if (filterType !== 'year' && selectedYear !== 'Year (ALL)' && !availableOptions.years.includes(parseInt(selectedYear, 10))) {
            setSelectedYear('Year (ALL)');
        }
        
        if (filterType !== 'issuingBody' && selectedIssuingBody !== 'Issuing Body (ALL)' && !availableOptions.issuingBodies.includes(selectedIssuingBody)) {
            setSelectedIssuingBody('Issuing Body (ALL)');
        }
        
        if (filterType !== 'status' && selectedStatus !== 'Status (ALL)' && !availableOptions.statuses.includes(selectedStatus)) {
            setSelectedStatus('Status (ALL)');
        }
        
        if (filterType !== 'category' && selectedCategory !== 'Category (ALL)' && !availableOptions.categories.includes(selectedCategory)) {
            setSelectedCategory('Category (ALL)');
        }
        
        // Reset to page 1
        setCurrentPage(1);
    };

    // Update filtered options using dynamic filtering
    useEffect(() => {
        const dynamicOptions = getDynamicFilterOptions();
        setFilteredYears(dynamicOptions.years);
        setFilteredIssuingBodies(dynamicOptions.issuingBodies);
        setFilteredStatuses(dynamicOptions.statuses);
        setFilteredCategories(dynamicOptions.categories);
    }, [selectedYear, selectedIssuingBody, selectedStatus, selectedCategory, searchQuery, data]);

    // All filtered data (for calculating totals)
    const allFilteredData = useMemo(() => {
        return data.filter(item => {
            const yearMatch = selectedYear === 'Year (ALL)' || item.year === parseInt(selectedYear, 10);
            const statusMatch = selectedStatus === 'Status (ALL)' || item.status === selectedStatus;
            const categoryMatch = selectedCategory === 'Category (ALL)' || item.category === selectedCategory;
            const issuingBodyMatch = selectedIssuingBody === 'Issuing Body (ALL)' || item.ib === selectedIssuingBody;
            const searchMatch = searchQuery
                ? item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.ib?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.wg?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.status?.toLowerCase().includes(searchQuery.toLowerCase())
                : true;
            return yearMatch && statusMatch && categoryMatch && issuingBodyMatch && searchMatch;
        });
    }, [data, selectedYear, selectedStatus, selectedCategory, selectedIssuingBody, searchQuery]);

    // Pagination calculations
    const paginationInfo = useMemo(() => {
        // After filtering, sort for cards and table: Live at the top, then most recent dates
        const sortedData = [...allFilteredData].sort((a, b) => {
            if (a.status === 'Live' && b.status !== 'Live') return -1;
            if (b.status === 'Live' && a.status !== 'Live') return 1;
            const aDate = a.completed && a.completed !== 'No Date Available' ? new Date(a.completed) : new Date(0);
            const bDate = b.completed && b.completed !== 'No Date Available' ? new Date(b.completed) : new Date(0);
            return bDate - aDate;
        });

        const totalCount = sortedData.length;
        const totalPagesCount = Math.ceil(totalCount / pageSize);
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const pageData = sortedData.slice(startIndex, endIndex);
        
        return {
            totalItems: totalCount,
            totalPages: totalPagesCount,
            currentPageData: pageData,
            startIndex,
            endIndex
        };
    }, [allFilteredData, currentPage, pageSize]);

    // Update pagination state when calculations change
    useMemo(() => {
        setTotalItems(paginationInfo.totalItems);
        setTotalPages(paginationInfo.totalPages);
    }, [paginationInfo.totalItems, paginationInfo.totalPages]);

    // Current page data for display
    const filteredConsultationsData = paginationInfo.currentPageData;

    /**
     * Handle page change with loading state and smooth scrolling
     * 
     * @param {number} newPage - The page number to navigate to
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
            setLoadingPageNumber(newPage);
            setCurrentPage(newPage);
            setIsLoadingPage(true);
            
            // Scroll to top of content
            document.querySelector('.content-section')?.scrollIntoView({ behavior: 'smooth' });
            
            // Simulate loading delay
            setTimeout(() => {
                setIsLoadingPage(false);
            }, 300);
        }
    };

    /**
     * Handle page size change and reset to first page
     * 
     * @param {number} newPageSize - The new page size
     */
    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to page 1 with new page size
        setLoadingPageNumber(1);
    };

    /**
     * Check if any filters are currently active
     * 
     * @returns {boolean} True if any filters are active
     */
    const isFilterActive = () =>
        selectedYear !== 'Year (ALL)' ||
        selectedStatus !== 'Status (ALL)' ||
        selectedCategory !== 'Category (ALL)' ||
        selectedIssuingBody !== 'Issuing Body (ALL)' ||
        searchQuery !== '' ||
        pageSize !== (activeTabIndex === 0 ? 6 : 10);

    /**
     * Get array of applied filters for display
     * 
     * @returns {Array} Array of filter objects with label, value, and remove function
     */
    const getAppliedFilters = () => {
        const appliedFilters = [];
        
        if (searchQuery) {
            appliedFilters.push({
                type: 'search',
                label: 'Search',
                value: searchQuery,
                onRemove: () => {
                    setSearchQuery('');
                    setCurrentPage(1);
                }
            });
        }
        
        if (selectedYear !== 'Year (ALL)') {
            appliedFilters.push({
                type: 'year',
                label: 'Year',
                value: selectedYear,
                onRemove: () => {
                    setSelectedYear('Year (ALL)');
                    setCurrentPage(1);
                }
            });
        }
        
        if (selectedStatus !== 'Status (ALL)') {
            appliedFilters.push({
                type: 'status',
                label: 'Status',
                value: selectedStatus,
                onRemove: () => {
                    setSelectedStatus('Status (ALL)');
                    setCurrentPage(1);
                }
            });
        }
        
        if (selectedCategory !== 'Category (ALL)') {
            appliedFilters.push({
                type: 'category',
                label: 'Category',
                value: selectedCategory,
                onRemove: () => {
                    setSelectedCategory('Category (ALL)');
                    setCurrentPage(1);
                }
            });
        }
        
        if (selectedIssuingBody !== 'Issuing Body (ALL)') {
            appliedFilters.push({
                type: 'issuingBody',
                label: 'Issuing Body',
                value: selectedIssuingBody,
                onRemove: () => {
                    setSelectedIssuingBody('Issuing Body (ALL)');
                    setCurrentPage(1);
                }
            });
        }
        
        return appliedFilters;
    };

    /**
     * Reset all filters to default state
     */
    const resetFilters = () => {
        setSelectedYear('Year (ALL)');
        setSelectedStatus('Status (ALL)');
        setSelectedCategory('Category (ALL)');
        setSelectedIssuingBody('Issuing Body (ALL)');
        setSearchQuery('');
        setCurrentPage(1);
        setPageSize(activeTabIndex === 0 ? 6 : 10);
        // Dynamic options will be updated by the useEffect automatically
    };

    const handleYearChange = (e) => {
        handleSmartFilterChange('year', e.target.value);
    };
    const handleStatusChange = (e) => {
        handleSmartFilterChange('status', e.target.value);
    };
    const handleCategoryChange = (e) => {
        handleSmartFilterChange('category', e.target.value);
    };
    const handleIssuingBodyChange = (e) => {
        handleSmartFilterChange('issuingBody', e.target.value);
    };
    const handleSearchChange = (query) => {
        setSearchQuery(query);
        setCurrentPage(1);
        // Dynamic filter options will update automatically via useEffect
    };

    const handleTabChange = (index) => {
        setActiveTabIndex(index);
        if (index === 1) {
            setPageSize(10); // Set to 10 for Table View
        } else {
            setPageSize(6); // Set to 6 for Card View
        }
        setCurrentPage(1); // Reset to first page when changing tabs
    };

    // Page resets are now handled by smart filter handlers

    return (
        <div id="consultations">
            {showChart && (
                <div className="cpstopcontainer pb-6">
                    <div className="cpstopcontainer-left">
                        <ConsultationsChart data={allFilteredData} />
                    </div>
                    <div className="cpstopcontainer-right cpstopcontainersummary">
                        {cspBlobContent?.content && typeof cspBlobContent.content === 'string' ? Parser(cspBlobContent.content, parserOptions) : ''}
                    </div>
                </div>
            )}
            
            <DaireSidebar
                isOpen={isDaireOpen}
                onClose={() => setIsDaireOpen(false)}
                isMinimized={isDaireMinimized}
                onMinimizeChange={setIsDaireMinimized}
            />

            <Tabs selectedIndex={activeTabIndex} onSelect={handleTabChange}>
                {/* ResearchCentre-style filters - single row layout */}
                <div className="filters-section">
                    <div className="filters-bar">
                        <div className="search-and-controls-row">
                            {showSearch && (
                                <div className="consultations-search-box">
                                    <i className="fas fa-search"></i>
                                    <input
                                        type="text"
                                        placeholder="Search consultations..."
                                        value={searchQuery}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                    />
                                    {searchQuery && (
                                        <i 
                                            className="fas fa-times clear-search" 
                                            onClick={() => handleSearchChange('')}
                                        />
                                    )}
                                </div>
                            )}

                            {/* Inline Filter Controls */}
                            <div className="inline-filter-controls">
                                <select value={selectedYear} onChange={handleYearChange}>
                                    {filteredYears.map(year => (
                                        <option key={year} value={year}>{year === 0 ? 'No Year' : year}</option>
                                    ))}
                                </select>

                                <select value={selectedIssuingBody} onChange={handleIssuingBodyChange}>
                                    {filteredIssuingBodies.map(ib => (
                                        <option key={ib} value={ib}>{ib}</option>
                                    ))}
                                </select>

                                <select value={selectedStatus} onChange={handleStatusChange}>
                                    {filteredStatuses.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>

                                <select value={selectedCategory} onChange={handleCategoryChange}>
                                    {filteredCategories.map(category => (
                                        <option key={category} value={category}>{category}</option>
                                    ))}
                                </select>

                                <select
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                >
                                    {activeTabIndex === 0 ? (
                                        <>
                                            <option value={6}>6 per page</option>
                                            <option value={12}>12 per page</option>
                                            <option value={18}>18 per page</option>
                                            <option value={36}>36 per page</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value={10}>10 per page</option>
                                            <option value={20}>20 per page</option>
                                            <option value={30}>30 per page</option>
                                            <option value={50}>50 per page</option>
                                        </>
                                    )}
                                </select>

                                {isFilterActive() && showReset && (
                                    <i className="fa-solid fa-rotate-left resetIcon" title='Reset Filters' onClick={resetFilters}></i>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Panel & View Controls - separate row exactly like ResearchCentre */}
                <div className="consultations-filters-status">
                    <div className="applied-filters-section">
                        {getAppliedFilters().length > 0 && (
                            <div className="applied-filters">
                                <span className="applied-filters-label">Applied filters:</span>
                                <div className="filter-tags">
                                    {getAppliedFilters().map((filter, index) => (
                                        <div key={`${filter.type}-${index}`} className="filter-tag">
                                            <span className="filter-tag-label">{filter.label}:</span>
                                            <span className="filter-tag-value">{filter.value}</span>
                                            <button 
                                                className="filter-tag-remove"
                                                onClick={filter.onRemove}
                                                title={`Remove ${filter.label} filter`}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="instant-stats-panel">
                        <div className="stats-info">
                            <div className="stats-description">
                                <i className="fa-solid fa-chart-simple"></i>
                                {totalItems === 1 ? '1 consultation' : `${totalItems} consultations`} found
                            </div>
                            {totalItems < data.length && (
                                <div className="stats-breakdown">
                                    <i className="fas fa-filter"></i>
                                    Filtered from {data.length.toLocaleString()} total
                                </div>
                            )}
                        </div>
                        
                        <div className="view-controls">
                            <button 
                                className={`view-btn ${activeTabIndex === 0 ? 'active' : ''}`}
                                onClick={() => handleTabChange(0)}
                                title="Card View"
                            >
                                <i className="fas fa-th"></i>
                            </button>
                            <button 
                                className={`view-btn ${activeTabIndex === 1 ? 'active' : ''}`}
                                onClick={() => handleTabChange(1)}
                                title="Table View"
                            >
                                <i className="fas fa-list"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Hidden TabList for react-tabs functionality */}
                <TabList style={{ display: 'none' }}>
                    <Tab>Card View</Tab>
                    <Tab>Table View</Tab>
                </TabList>

                <TabPanel>
                    {isLoadingPage && (
                        <div className="page-loading">
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Loading page {loadingPageNumber}...</span>
                        </div>
                    )}
                    {totalItems === 0 ? (
                        <div className="no-results">
                            <div className="no-results-content">
                                <i className="fas fa-search"></i>
                                <h3>No consultations found</h3>
                                <p>Try adjusting your search criteria or filters to see more results.</p>
                            </div>
                        </div>
                    ) : (
                        <ConsultationsCards data={filteredConsultationsData} visibleCount={pageSize} searchQuery={searchQuery} />
                    )}
                </TabPanel>
                <TabPanel>
                    {isLoadingPage && (
                        <div className="page-loading">
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Loading page {loadingPageNumber}...</span>
                        </div>
                    )}
                    {totalItems === 0 ? (
                        <div className="no-results">
                            <div className="no-results-content">
                                <i className="fas fa-search"></i>
                                <h3>No consultations found</h3>
                                <p>Try adjusting your search criteria or filters to see more results.</p>
                            </div>
                        </div>
                    ) : (
                        <ConsultationsTable data={filteredConsultationsData} visibleCount={pageSize} searchQuery={searchQuery} />
                    )}
                </TabPanel>

                {/* Pagination */}
                {totalPages > 1 && !isLoadingPage && (
                    <div className="pagination">
                        <div className="pagination-info">
                            Page {currentPage} of {totalPages}
                        </div>
                        <div className="pagination-controls">
                            <button 
                                className="pagination-btn"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                            >
                                <i className="fas fa-angle-double-left"></i>
                            </button>
                            <button 
                                className="pagination-btn"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                <i className="fas fa-angle-left"></i>
                            </button>
                            
                            {/* Page numbers */}
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                
                                return (
                                    <button
                                        key={pageNum}
                                        className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            
                            <button 
                                className="pagination-btn"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                <i className="fas fa-angle-right"></i>
                            </button>
                            <button 
                                className="pagination-btn"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                            >
                                <i className="fas fa-angle-double-right"></i>
                            </button>
                        </div>
                    </div>
                )}
            </Tabs>
        </div>
    );
}

export default Consultations;
